import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { Modal, Button, Spin, Image, Steps, Switch, Tooltip } from 'antd'
import {
  ArrowLeftOutlined,
  ArrowRightOutlined,
  CheckCircleFilled,
  CheckOutlined,
  ClearOutlined,
  CloseOutlined,
  DeleteOutlined,
  DownloadOutlined,
  PlusOutlined,
  SaveOutlined,
  EditOutlined,
  LeftOutlined,
  PictureOutlined,
  RightOutlined,
  UndoOutlined,
  WarningFilled,
  ZoomInOutlined,
  ZoomOutOutlined,
} from '@ant-design/icons'
import { toast } from 'react-toastify'
import { postFirmarRevision } from 'src/services/axiomaService'
import {
  COLOR_COTA,
  ESTILO_COTA_DEFECTO,
  cotaEsVertical,
  MAX_FOTOS_COTIZACION,
  ajustarAEje,
  cargarImagenExportable,
  generarImagenCotizacion,
  geometriaCota,
  gsUrlDesdeUrlFirmada,
  hidratarCotizacion,
  parseJsonCotizacion,
  posicionesIniciales,
  calcularRecorte,
  cajaCota,
  tamanoVisible,
  SALIDA_ALTO,
  serializarCotizacion,
  unidadCota,
} from '../utils/cotizacionImagenUtils'
import ComposicionCotizacion from './ComposicionCotizacion'
import BarraEstiloCota from './BarraEstiloCota'
import './ModalImagenCotizacion.scss'

const PASOS = ['seleccion', 'editor', 'resultado']
const EXTREMOS = ['inicio', 'fin']

const valorPresente = (valor) =>
  valor !== null && valor !== undefined && String(valor).trim() !== '' && String(valor).trim() !== '0'

// La descripción de la plantilla suele venir como "A: DIAMETRO DEL RODILLO";
// la letra ya se muestra aparte, así que se quita ese prefijo.
const sinPrefijoLetra = (texto, letra) => {
  const escapada = letra.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
  return texto.replace(new RegExp(`^\\s*${escapada}\\s*[:.)-]\\s*`, 'i'), '').trim()
}

// Algunas plantillas juntan varias piezas (ej. kit de arrastre: catalina,
// piñón y cadena) y repiten letras: cada pieza vuelve a empezar en A. Una letra
// repetida dentro del mismo bloque abre el bloque siguiente, y las letras que
// aparecen más de una vez se numeran con su bloque (A1, A2...). Las que no se
// repiten quedan igual.
const numerarLetrasRepetidas = (medidas) => {
  let bloque = 1
  let vistas = new Set()
  const conBloque = medidas.map((m) => {
    if (vistas.has(m.letra)) {
      bloque += 1
      vistas = new Set()
    }
    vistas.add(m.letra)
    return { ...m, bloque }
  })
  const veces = conBloque.reduce((acc, m) => acc.set(m.letra, (acc.get(m.letra) || 0) + 1), new Map())
  return conBloque.map(({ bloque: b, ...m }) => ({
    ...m,
    letraBase: m.letra,
    letra: veces.get(m.letra) > 1 ? `${m.letra}${b}` : m.letra,
  }))
}

const plural = (n, uno, varios) => `${n} ${n === 1 ? uno : varios}`

// ==================== Cota en SVG (misma geometría que la exportación) ====================
const CotaSvg = ({ cota, letra, s, seleccionada = false, borrador = false }) => {
  const g = geometriaCota(cota, s)
  const trazos = (extra, color) => (
    <>
      {g.guias.map(([ax, ay, bx, by], i) => (
        <line
          key={`guia-${EXTREMOS[i]}`}
          x1={ax}
          y1={ay}
          x2={bx}
          y2={by}
          stroke={color}
          strokeWidth={g.guiaGrosor + extra}
          strokeDasharray={g.guiaGuiones?.join(' ')}
          strokeLinecap="round"
        />
      ))}
      {g.lineas.map(([ax, ay, bx, by], i) => (
        // eslint-disable-next-line react/no-array-index-key
        <line key={`l-${i}`} x1={ax} y1={ay} x2={bx} y2={by} stroke={color} strokeWidth={g.grosor + extra} strokeLinecap="round" />
      ))}
      {g.globo && (
        <circle cx={g.globo.x} cy={g.globo.y} r={g.globo.r} fill="none" stroke={color} strokeWidth={g.grosor + extra} />
      )}
      {g.circunferencia && (
        <circle
          cx={g.circunferencia.x}
          cy={g.circunferencia.y}
          r={g.circunferencia.r}
          fill="none"
          stroke={color}
          strokeWidth={g.grosor + extra}
          strokeDasharray={g.circunferenciaGuiones?.join(' ')}
        />
      )}
      {g.topes.map(([ax, ay, bx, by], i) => (
        <line key={EXTREMOS[i]} x1={ax} y1={ay} x2={bx} y2={by} stroke={color} strokeWidth={g.grosor + extra} strokeLinecap="round" />
      ))}
    </>
  )
  return (
    <g className={`cotz-cota ${borrador ? 'is-borrador' : ''}`}>
      {seleccionada && (
        <>
          {g.lineas.map(([ax, ay, bx, by], i) => (
            // eslint-disable-next-line react/no-array-index-key
            <line
              key={`sel-${i}`}
              x1={ax}
              y1={ay}
              x2={bx}
              y2={by}
              stroke="rgba(22, 119, 255, 0.28)"
              strokeWidth={3.4 * s * 7}
              strokeLinecap="round"
            />
          ))}
          {g.globo && (
            <circle cx={g.globo.x} cy={g.globo.y} r={g.globo.r + 3.4 * s * 3} fill="rgba(22, 119, 255, 0.28)" />
          )}
          {g.circunferencia && (
            <circle
              cx={g.circunferencia.x}
              cy={g.circunferencia.y}
              r={g.circunferencia.r}
              fill="none"
              stroke="rgba(22, 119, 255, 0.28)"
              strokeWidth={3.4 * s * 7}
            />
          )}
        </>
      )}
      {trazos(g.halo * 0.6, g.colorHaloLinea)}
      {g.globo && <circle cx={g.globo.x} cy={g.globo.y} r={g.globo.r} fill={g.globo.relleno} />}
      {trazos(0, g.color)}
      {g.flechas.map((p, i) => (
        <polygon key={EXTREMOS[i]} points={p.map((xy) => xy.join(',')).join(' ')} fill={g.color} />
      ))}
      {g.puntos.map(([px, py], i) => (
        <circle key={EXTREMOS[i]} cx={px} cy={py} r={g.radioPunto} fill={g.color} />
      ))}
      <text
        x={g.etiqueta.x}
        y={g.etiqueta.y}
        fontSize={g.etiqueta.fuente}
        fontWeight={800}
        fontFamily='"Helvetica Neue", Arial, sans-serif'
        textAnchor="middle"
        dominantBaseline="central"
        fill={g.color}
        // Dentro del globo la letra ya tiene fondo: no lleva halo.
        stroke={g.globo ? 'none' : g.colorHalo}
        strokeWidth={g.halo}
        paintOrder="stroke"
        strokeLinejoin="round"
      >
        {letra}
      </text>
    </g>
  )
}

// ==================== Lienzo de una foto: trazar, seleccionar y ajustar cotas ====================
const ZOOM_MIN = 1
const ZOOM_MAX = 10
const ZOOM_PASO = 1.25

// Tipo de cota (medida/globo, con su forma de línea y punta), grosor y tamaño
// de letra se recuerdan entre medidas y entre aperturas del modal (preferencia
// del navegador; si el storage falla se usa el defecto). Cada campo trae su
// validación para ignorar valores viejos o corruptos del storage.
const CLAVE_ESTILO_NUEVAS = 'axioma.cotizacion.estiloCotas'
const CAMPOS_ESTILO_PERSISTENTE = {
  grosor: (v) => typeof v === 'number',
  letraTamano: (v) => typeof v === 'number',
  tipo: (v) => v === 'medida' || v === 'globo' || v === 'circunferencia',
  lineaGlobo: (v) => v === 'codo' || v === 'recta',
  puntaGlobo: (v) => v === 'flecha' || v === 'redonda' || v === 'ninguna',
  trazoCircunferencia: (v) => v === 'continua' || v === 'discontinua',
}

const camposPersistentes = (origen) =>
  Object.fromEntries(
    Object.entries(CAMPOS_ESTILO_PERSISTENTE)
      .filter(([campo, valido]) => valido(origen?.[campo]))
      .map(([campo]) => [campo, origen[campo]]),
  )

const leerEstiloNuevas = () => {
  try {
    const guardado = JSON.parse(window.localStorage.getItem(CLAVE_ESTILO_NUEVAS) || '{}')
    return camposPersistentes(guardado)
  } catch {
    return {}
  }
}

// Área de trabajo extra alrededor de la foto (en px de la foto) para que las
// cotas puedan salirse de ella. Crece sola al trazar cerca de un borde.
const SIN_EXTENSION = { l: 0, t: 0, r: 0, b: 0 }
const EXTENSION_UMBRAL = 0.03 // cercanía al borde que dispara el crecimiento
const EXTENSION_PASO = 0.1 // cuánto crece cada vez
const EXTENSION_MAX = 0.6 // máximo por lado
const EXTENSION_INTERVALO_MS = 200

const tieneExtension = (e) => !!e && (e.l > 0 || e.t > 0 || e.r > 0 || e.b > 0)

/** Extensión mínima para que todas las cotas quepan (lo que sobresale de la foto). */
const extensionMinima = (cotas, W, H) => {
  const s = unidadCota(W, H)
  return Object.entries(cotas || {}).reduce((acc, [letra, cota]) => {
    const c = cajaCota(cota, s, letra)
    return {
      l: Math.max(acc.l, -c.x0),
      t: Math.max(acc.t, -c.y0),
      r: Math.max(acc.r, c.x1 - W),
      b: Math.max(acc.b, c.y1 - H),
    }
  }, SIN_EXTENSION)
}

const esCampoTexto = (el) => el?.tagName === 'INPUT' || el?.tagName === 'TEXTAREA' || el?.isContentEditable

const LienzoFoto = ({
  fuente,
  cotas,
  medidaActiva,
  cotaSeleccionada,
  onCrear,
  onIniciarAjuste,
  onAjustar,
  onSeleccionar,
  onVoltear,
  onBorradorChange,
  estiloNuevas = {},
  extension = SIN_EXTENSION,
  onExtender,
}) => {
  const escenarioRef = useRef(null)
  const lienzoRef = useRef(null)
  const svgRef = useRef(null)
  const inicioClienteRef = useRef(null)
  const arrastreRef = useRef(null)
  const [tamano, setTamano] = useState(null)
  const [borrador, setBorrador] = useState(null)

  // Zoom: el lienzo crece dentro del escenario (que hace scroll sin barras) y
  // el punto bajo el cursor se mantiene fijo al acercar/alejar.
  const [zoom, setZoom] = useState(1)
  const zoomRef = useRef(1)
  const anclaZoomRef = useRef(null)
  const espacioRef = useRef(false)
  const panRef = useRef(null)
  const [conEspacio, setConEspacio] = useState(false)
  const [paneando, setPaneando] = useState(false)

  const { ancho: W, alto: H } = fuente
  const s = unidadCota(W, H)
  const ext = extension
  // Tamaño total del área de trabajo: foto + extensión.
  const TW = W + ext.l + ext.r
  const TH = H + ext.t + ext.b
  const ultimaExtensionRef = useRef(0)

  useEffect(() => {
    const el = escenarioRef.current
    if (!el) return undefined
    const medir = () => {
      const escala = Math.min(el.clientWidth / TW, el.clientHeight / TH)
      setTamano({ w: TW * escala, h: TH * escala, escala })
    }
    medir()
    const ro = new ResizeObserver(medir)
    ro.observe(el)
    return () => ro.disconnect()
  }, [TW, TH])

  const aplicarZoom = useCallback((nuevo, clienteX, clienteY) => {
    const z = Math.min(Math.max(nuevo, ZOOM_MIN), ZOOM_MAX)
    if (Math.abs(z - zoomRef.current) < 0.001) return
    const lienzo = lienzoRef.current
    const esc = escenarioRef.current
    if (lienzo && esc) {
      const r = lienzo.getBoundingClientRect()
      const er = esc.getBoundingClientRect()
      const cx = clienteX ?? er.left + er.width / 2
      const cy = clienteY ?? er.top + er.height / 2
      anclaZoomRef.current = { fx: (cx - r.left) / r.width, fy: (cy - r.top) / r.height, cx, cy }
    }
    zoomRef.current = z
    setZoom(z)
  }, [])

  // Tras redimensionar el lienzo, mover el scroll para que el punto anclado
  // quede otra vez bajo el cursor.
  useLayoutEffect(() => {
    const a = anclaZoomRef.current
    const lienzo = lienzoRef.current
    const esc = escenarioRef.current
    anclaZoomRef.current = null
    if (!a || !lienzo || !esc) return
    const r = lienzo.getBoundingClientRect()
    esc.scrollLeft += r.left + a.fx * r.width - a.cx
    esc.scrollTop += r.top + a.fy * r.height - a.cy
  }, [zoom])

  // Ctrl/Cmd + rueda (o pellizco en trackpad) = zoom; rueda sola = mover.
  // Listener nativo porque React registra `wheel` como pasivo.
  useEffect(() => {
    const el = escenarioRef.current
    if (!el) return undefined
    const onWheel = (e) => {
      const factor = e.deltaMode === 1 ? 16 : 1
      if (e.ctrlKey || e.metaKey) {
        e.preventDefault()
        aplicarZoom(zoomRef.current * Math.exp(-e.deltaY * factor * 0.0025), e.clientX, e.clientY)
      } else if (zoomRef.current > 1) {
        e.preventDefault()
        el.scrollLeft += (e.shiftKey ? e.deltaY : e.deltaX) * factor
        el.scrollTop += (e.shiftKey ? 0 : e.deltaY) * factor
      }
    }
    el.addEventListener('wheel', onWheel, { passive: false })
    return () => el.removeEventListener('wheel', onWheel)
  }, [aplicarZoom])

  // Teclado: + / - / 0 para zoom; mantener Espacio para mover con el mouse.
  useEffect(() => {
    const onDown = (e) => {
      if (esCampoTexto(e.target) || e.metaKey || e.ctrlKey || e.altKey) return
      if (e.key === ' ') {
        e.preventDefault()
        if (!espacioRef.current) {
          espacioRef.current = true
          setConEspacio(true)
        }
      } else if (e.key === '+' || e.key === '=') {
        aplicarZoom(zoomRef.current * ZOOM_PASO)
      } else if (e.key === '-' || e.key === '_') {
        aplicarZoom(zoomRef.current / ZOOM_PASO)
      } else if (e.key === '0') {
        aplicarZoom(1)
      }
    }
    const onUp = (e) => {
      if (e.key !== ' ') return
      if (!esCampoTexto(e.target)) e.preventDefault()
      espacioRef.current = false
      setConEspacio(false)
    }
    window.addEventListener('keydown', onDown)
    window.addEventListener('keyup', onUp)
    return () => {
      window.removeEventListener('keydown', onDown)
      window.removeEventListener('keyup', onUp)
    }
  }, [aplicarZoom])

  // Mover la vista: botón central, o Espacio + clic. Se intercepta en captura
  // para que no empiece un trazo ni se arrastre una cota.
  const iniciarPan = (e) => {
    if (!(e.button === 1 || (e.button === 0 && espacioRef.current))) return
    e.preventDefault()
    e.stopPropagation()
    const el = escenarioRef.current
    panRef.current = { x: e.clientX, y: e.clientY, sl: el.scrollLeft, st: el.scrollTop }
    el.setPointerCapture(e.pointerId)
    setPaneando(true)
  }

  const moverPan = (e) => {
    const p = panRef.current
    if (!p) return
    const el = escenarioRef.current
    el.scrollLeft = p.sl - (e.clientX - p.x)
    el.scrollTop = p.st - (e.clientY - p.y)
  }

  const terminarPan = () => {
    if (!panRef.current) return
    panRef.current = null
    setPaneando(false)
  }

  // Avisar al padre si hay un trazo a medio hacer (para el texto de ayuda)
  useEffect(() => {
    onBorradorChange?.(borrador?.modo ?? null)
  }, [borrador?.modo, onBorradorChange])

  // Al cambiar de foto o de medida se descarta el trazo pendiente
  useEffect(() => {
    setBorrador(null)
  }, [fuente, medidaActiva])

  useEffect(() => {
    if (!borrador) return undefined
    const onKey = (e) => {
      if (e.key === 'Escape') {
        e.stopPropagation()
        setBorrador(null)
      }
    }
    window.addEventListener('keydown', onKey, true)
    return () => window.removeEventListener('keydown', onKey, true)
  }, [borrador])

  // Unidades de la foto por px de pantalla: manijas y zonas de agarre se
  // dibujan con esto para que no cambien de tamaño al hacer zoom.
  const k = tamano ? 1 / (tamano.escala * zoom) : 1

  const punto = (e) => {
    const r = svgRef.current.getBoundingClientRect()
    const x = -ext.l + ((e.clientX - r.left) / r.width) * TW
    const y = -ext.t + ((e.clientY - r.top) / r.height) * TH
    return { x: Math.min(Math.max(x, -ext.l), W + ext.r), y: Math.min(Math.max(y, -ext.t), H + ext.b) }
  }

  // Al trazar o arrastrar cerca de un borde del área, esta crece hacia ese
  // lado (como mucho cada EXTENSION_INTERVALO_MS, para que no se dispare).
  const extenderSiCerca = (p) => {
    if (!onExtender) return
    const ahora = performance.now()
    if (ahora - ultimaExtensionRef.current < EXTENSION_INTERVALO_MS) return
    const base = Math.max(W, H)
    const umbral = base * EXTENSION_UMBRAL
    const paso = base * EXTENSION_PASO
    const max = base * EXTENSION_MAX
    const nueva = { ...ext }
    if (p.x <= -ext.l + umbral) nueva.l = Math.min(max, ext.l + paso)
    if (p.x >= W + ext.r - umbral) nueva.r = Math.min(max, ext.r + paso)
    if (p.y <= -ext.t + umbral) nueva.t = Math.min(max, ext.t + paso)
    if (p.y >= H + ext.b - umbral) nueva.b = Math.min(max, ext.b + paso)
    if (nueva.l === ext.l && nueva.r === ext.r && nueva.t === ext.t && nueva.b === ext.b) return
    ultimaExtensionRef.current = ahora
    onExtender(nueva)
  }

  const esCircunferencia = (estiloNuevas?.tipo || ESTILO_COTA_DEFECTO.tipo) === 'circunferencia'

  const finalizar = (b, p, libre) => {
    // Circunferencia: el segundo punto es el borde; no tiene sentido alinear a ejes.
    const q = libre || esCircunferencia ? p : ajustarAEje(b.x1, b.y1, p.x, p.y)
    setBorrador(null)
    // Trazos de menos de ~10 px en pantalla se ignoran
    if (Math.hypot(q.x - b.x1, q.y - b.y1) / k < 10) return
    onCrear(medidaActiva, { x1: b.x1, y1: b.y1, x2: q.x, y2: q.y, lado: 1 })
  }

  const handlePointerDown = (e) => {
    if (e.button !== 0) return
    const p = punto(e)
    if (borrador?.modo === 'clic') {
      finalizar(borrador, p, e.shiftKey)
      return
    }
    onSeleccionar(null)
    if (!medidaActiva) return
    svgRef.current.setPointerCapture(e.pointerId)
    inicioClienteRef.current = { x: e.clientX, y: e.clientY }
    setBorrador({ x1: p.x, y1: p.y, x2: p.x, y2: p.y, modo: 'arrastre' })
  }

  const handlePointerMove = (e) => {
    const p = punto(e)
    const arrastre = arrastreRef.current
    if (arrastre || borrador) extenderSiCerca(p)
    if (arrastre) {
      // El historial se guarda al primer movimiento real, no con un simple clic
      if (!arrastre.guardado) {
        onIniciarAjuste()
        arrastre.guardado = true
      }
      const c = arrastre.original
      let nueva
      if (arrastre.extremo === 0) {
        const dx = p.x - arrastre.origen.x
        const dy = p.y - arrastre.origen.y
        nueva = { ...c, x1: c.x1 + dx, y1: c.y1 + dy, x2: c.x2 + dx, y2: c.y2 + dy }
      } else if (arrastre.extremo === 1) {
        if (c.tipo === 'circunferencia') {
          // Centro: traslada el círculo entero (el borde sigue al centro).
          const dx = p.x - arrastre.origen.x
          const dy = p.y - arrastre.origen.y
          nueva = { ...c, x1: c.x1 + dx, y1: c.y1 + dy, x2: c.x2 + dx, y2: c.y2 + dy }
        } else {
          const q = e.shiftKey ? p : ajustarAEje(c.x2, c.y2, p.x, p.y)
          nueva = { ...c, x1: q.x, y1: q.y }
        }
      } else {
        const libre = e.shiftKey || c.tipo === 'circunferencia'
        const q = libre ? p : ajustarAEje(c.x1, c.y1, p.x, p.y)
        nueva = { ...c, x2: q.x, y2: q.y }
      }
      onAjustar(arrastre.letra, nueva)
      return
    }
    if (borrador) {
      const libre = e.shiftKey || esCircunferencia
      const q = libre ? p : ajustarAEje(borrador.x1, borrador.y1, p.x, p.y)
      setBorrador((b) => (b ? { ...b, x2: q.x, y2: q.y } : b))
    }
  }

  const handlePointerUp = (e) => {
    if (arrastreRef.current) {
      arrastreRef.current = null
      return
    }
    if (borrador?.modo === 'arrastre') {
      const ini = inicioClienteRef.current
      const movido = ini ? Math.hypot(e.clientX - ini.x, e.clientY - ini.y) : 0
      if (movido < 6) {
        // Fue un clic: queda esperando el segundo clic para el punto final
        setBorrador((b) => (b ? { ...b, modo: 'clic' } : b))
      } else {
        finalizar(borrador, punto(e), e.shiftKey)
      }
    }
  }

  const iniciarArrastre = (e, letra, extremo) => {
    if (borrador) return // con un trazo pendiente, el clic lo completa el lienzo
    e.stopPropagation()
    if (e.button !== 0) return
    svgRef.current.setPointerCapture(e.pointerId)
    onSeleccionar(letra)
    arrastreRef.current = { letra, extremo, origen: punto(e), original: cotas[letra], guardado: false }
  }

  const seleccion = cotaSeleccionada ? cotas[cotaSeleccionada] : null

  return (
    <div className="cotz-escenario-wrap">
    <div
      className={`cotz-escenario cotz-escenario--zoom ${conEspacio ? 'is-espacio' : ''} ${paneando ? 'is-paneando' : ''}`}
      ref={escenarioRef}
      onPointerDownCapture={iniciarPan}
      onPointerMove={moverPan}
      onPointerUp={terminarPan}
      onPointerCancel={terminarPan}
      // Evita el autoscroll del navegador con el botón central.
      onMouseDown={(e) => e.button === 1 && e.preventDefault()}
    >
      {tamano && (
        <div className="cotz-lienzo" ref={lienzoRef} style={{ width: tamano.w * zoom, height: tamano.h * zoom }}>
          <img
            src={fuente.objectUrl}
            alt=""
            draggable={false}
            style={{
              position: 'absolute',
              left: `${(ext.l / TW) * 100}%`,
              top: `${(ext.t / TH) * 100}%`,
              width: `${(W / TW) * 100}%`,
              height: `${(H / TH) * 100}%`,
            }}
          />
          <svg
            ref={svgRef}
            viewBox={`${-ext.l} ${-ext.t} ${TW} ${TH}`}
            className={medidaActiva || borrador ? 'is-trazando' : ''}
            onPointerDown={handlePointerDown}
            onPointerMove={handlePointerMove}
            onPointerUp={handlePointerUp}
            onPointerCancel={() => {
              arrastreRef.current = null
              setBorrador(null)
            }}
          >
            {tieneExtension(ext) && (
              // Borde de la foto original dentro del área ampliada.
              <rect
                x={0}
                y={0}
                width={W}
                height={H}
                fill="none"
                stroke="rgba(15, 23, 42, 0.25)"
                strokeWidth={k}
                strokeDasharray={`${6 * k} ${4 * k}`}
                pointerEvents="none"
              />
            )}
            {Object.entries(cotas).map(([letra, cota]) => {
              const g = geometriaCota(cota, s)
              return (
                <g key={letra}>
                  <CotaSvg cota={cota} letra={letra} s={s} seleccionada={cotaSeleccionada === letra} />
                  {/* Zona de agarre: más gruesa que la línea para que sea fácil tomarla */}
                  {g.lineas.map(([ax, ay, bx, by], i) => (
                    <line
                      // eslint-disable-next-line react/no-array-index-key
                      key={i}
                      className="cotz-agarre"
                      x1={ax}
                      y1={ay}
                      x2={bx}
                      y2={by}
                      strokeWidth={16 * k}
                      onPointerDown={(e) => iniciarArrastre(e, letra, 0)}
                    />
                  ))}
                  {g.circunferencia && (
                    <circle
                      className="cotz-agarre"
                      cx={g.circunferencia.x}
                      cy={g.circunferencia.y}
                      r={g.circunferencia.r}
                      fill="none"
                      strokeWidth={16 * k}
                      onPointerDown={(e) => iniciarArrastre(e, letra, 0)}
                    />
                  )}
                  <circle
                    className="cotz-agarre"
                    cx={g.etiqueta.x}
                    cy={g.etiqueta.y}
                    r={Math.max(g.etiqueta.fuente * 0.7, g.globo?.r ?? 0)}
                    onPointerDown={(e) => iniciarArrastre(e, letra, 0)}
                    onDoubleClick={() => onVoltear(letra)}
                  />
                </g>
              )
            })}

            {seleccion &&
              [1, 2].map((extremo) => (
                <circle
                  key={extremo}
                  className="cotz-manija"
                  cx={extremo === 1 ? seleccion.x1 : seleccion.x2}
                  cy={extremo === 1 ? seleccion.y1 : seleccion.y2}
                  r={7 * k}
                  strokeWidth={2.5 * k}
                  onPointerDown={(e) => iniciarArrastre(e, cotaSeleccionada, extremo)}
                />
              ))}

            {borrador && medidaActiva && (
              <>
                <circle cx={borrador.x1} cy={borrador.y1} r={5 * k} fill={COLOR_COTA} />
                {Math.hypot(borrador.x2 - borrador.x1, borrador.y2 - borrador.y1) / k > 4 && (
                  <CotaSvg cota={{ ...estiloNuevas, ...borrador }} letra={medidaActiva} s={s} borrador />
                )}
              </>
            )}
          </svg>
        </div>
      )}
    </div>
      <div className="cotz-zoom">
        {tieneExtension(ext) && (
          <Tooltip title="Quitar el espacio extra que no usan las cotas">
            <Button size="small" type="text" onClick={() => onExtender?.(extensionMinima(cotas, W, H))}>
              Ajustar área
            </Button>
          </Tooltip>
        )}
        <Tooltip title="Alejar (−)">
          <Button
            size="small"
            type="text"
            icon={<ZoomOutOutlined />}
            disabled={zoom <= ZOOM_MIN}
            onClick={() => aplicarZoom(zoom / ZOOM_PASO)}
          />
        </Tooltip>
        <Tooltip title="Ajustar a la pantalla (0)">
          <button type="button" className="cotz-zoom__valor" onClick={() => aplicarZoom(1)}>
            {Math.round(zoom * 100)}%
          </button>
        </Tooltip>
        <Tooltip title="Acercar (+)">
          <Button
            size="small"
            type="text"
            icon={<ZoomInOutlined />}
            disabled={zoom >= ZOOM_MAX}
            onClick={() => aplicarZoom(zoom * ZOOM_PASO)}
          />
        </Tooltip>
      </div>
      {zoom > 1 && (
        <div className="cotz-zoom__ayuda">Rueda o Espacio + arrastrar para moverte</div>
      )}
    </div>
  )
}

// ==================== Modal principal ====================
/**
 * Arma a mano la imagen de cotización: el usuario elige de 1 a 4 fotos, traza
 * sobre ellas las medidas de la plantilla (A, B, C...) guiándose con la imagen
 * de referencia, y genera una imagen con la tabla de medidas a la izquierda y
 * las fotos acotadas a la derecha.
 */
const ModalImagenCotizacion = ({
  open,
  onClose,
  fotos = [],
  fotosEmpaque = [],
  imagenFavorita = null,
  especificaciones = [],
  /** true si las especificaciones vienen del flujo "Agregar manualmente". */
  medidasSonManuales = false,
  imagenesPlantilla = [],
  cargandoPlantilla = false,
  nombreArchivo = 'material',
  imagenGuardada = null,
  jsonCotizacion = null,
  puedeCrearNueva = true,
  motivoNoCrear,
  onGuardar,
  onPersistirJson,
  /** Avisa al padre cuando se restauran medidas manuales desde json_cotizacion. */
  onMedidasManualesRestauradas,
  /** Si no hay medidas (flujo sin plantilla), "Crear nueva" pide armarlas a mano. */
  onPedirMedidasManuales,
}) => {
  const [paso, setPaso] = useState('seleccion')
  const [seleccion, setSeleccion] = useState([])
  const [fuentes, setFuentes] = useState({})
  const [cargandoFuentes, setCargandoFuentes] = useState(false)
  const [cotas, setCotas] = useState({})
  const [historial, setHistorial] = useState([])
  const [fotoActiva, setFotoActiva] = useState(null)
  const [medidaActiva, setMedidaActiva] = useState(null)
  const [cotaSeleccionada, setCotaSeleccionada] = useState(null)
  const [modoBorrador, setModoBorrador] = useState(null)
  const [guardando, setGuardando] = useState(false)
  // Grosor y tamaño de letra de la última cota editada: se aplican a las nuevas.
  const [estiloNuevas, setEstiloNuevas] = useState(leerEstiloNuevas)
  const [composicion, setComposicion] = useState(null)
  const [capas, setCapas] = useState([])
  // Textos libres sobre la imagen final (paso Guardar).
  const [textos, setTextos] = useState([])
  // Espacio extra alrededor de cada foto en el editor: { [nombre]: { l, t, r, b } }.
  const [extensiones, setExtensiones] = useState({})
  // Recortar cada foto a la pieza + sus cotas, sin el margen de fondo.
  const [quitarMargenes, setQuitarMargenes] = useState(true)
  const [indicePlantilla, setIndicePlantilla] = useState(0)
  // Specs restauradas desde json (manuales); pisan las props mientras el modal esté abierto.
  const [especificacionesLocal, setEspecificacionesLocal] = useState(null)
  const [manualLocal, setManualLocal] = useState(false)
  const fuentesRef = useRef({})

  const especificacionesEfectivas = especificacionesLocal ?? especificaciones ?? []
  const esManual = manualLocal || medidasSonManuales

  const medidas = useMemo(
    () =>
      numerarLetrasRepetidas(
        (especificacionesEfectivas || [])
          .map((e) => {
            const letra = String(e.medida ?? '').trim()
            return {
              letra,
              texto: sinPrefijoLetra(String(e.definicion_cotas ?? e.descripcion ?? e.nombre ?? ''), letra),
              valor: e.valor,
              esTexto: e.tipo_campo === 'Text',
            }
          })
          .filter((m) => m.letra),
      ),
    [especificacionesEfectivas],
  )
  const medidasTrazables = useMemo(() => medidas.filter((m) => !m.esTexto), [medidas])

  const fotosMaterial = useMemo(() => fotos.filter((f) => f?.signed_url), [fotos])
  const fotosDeEmpaque = useMemo(() => fotosEmpaque.filter((f) => f?.signed_url), [fotosEmpaque])
  // Material y empaque se eligen juntos; las de empaque se identifican por su
  // propio nombre_objeto (viven en otra carpeta del bucket).
  const fotosDisponibles = useMemo(() => [...fotosMaterial, ...fotosDeEmpaque], [fotosMaterial, fotosDeEmpaque])

  const liberarRecursos = useCallback(() => {
    Object.values(fuentesRef.current).forEach((f) => f?.revocar && URL.revokeObjectURL(f.objectUrl))
    fuentesRef.current = {}
  }, [])

  const resetEstadoLimpio = useCallback(() => {
    const favorita =
      fotosMaterial.find((f) => f.nombre_objeto === imagenFavorita) || fotosMaterial[0] || fotosDeEmpaque[0]
    setSeleccion(favorita ? [favorita.nombre_objeto] : [])
    setFuentes({})
    setCotas({})
    setHistorial([])
    setFotoActiva(null)
    setMedidaActiva(null)
    setCotaSeleccionada(null)
    setComposicion(null)
    setCapas([])
    setExtensiones({})
    setTextos([])
    setQuitarMargenes(true)
    setIndicePlantilla(0)
    setEspecificacionesLocal(null)
    setManualLocal(false)
  }, [fotosMaterial, fotosDeEmpaque, imagenFavorita])

  const cotasFoto = (fotoActiva && cotas[fotoActiva]) || {}
  const totalCotas = seleccion.reduce((acc, n) => acc + Object.keys(cotas[n] || {}).length, 0)
  const fotosConCotas = seleccion.filter((n) => Object.keys(cotas[n] || {}).length > 0).length

  const snapshotActual = useCallback(
    (pasoOverride) =>
      serializarCotizacion({
        paso: pasoOverride || (paso === 'resultado' ? 'resultado' : 'editor'),
        seleccion,
        cotas,
        extensiones,
        quitarMargenes,
        capas,
        textos,
        medidasManuales: esManual ? especificacionesEfectivas : null,
      }),
    [
      paso,
      seleccion,
      cotas,
      extensiones,
      quitarMargenes,
      capas,
      textos,
      esManual,
      especificacionesEfectivas,
    ],
  )

  const hayTrabajoEditable =
    totalCotas > 0 || !!composicion || (paso === 'resultado' && capas.length > 0)

  const cargarFuentesDe = async (nombres) => {
    const faltantes = nombres.filter((n) => !fuentesRef.current[n])
    if (!faltantes.length) return
    setCargandoFuentes(true)
    await Promise.all(
      faltantes.map(async (nombre) => {
        const foto = fotosDisponibles.find((f) => f.nombre_objeto === nombre)
        if (!foto?.signed_url) return
        try {
          const cargada = await cargarImagenExportable(foto.signed_url).catch(async (error) => {
            // Igual que con la foto de la cámara: volver a firmar el gs:// en el
            // backend da una URL que sí se puede descargar con CORS.
            const gsUrl = gsUrlDesdeUrlFirmada(foto.signed_url)
            const firmada = gsUrl ? await postFirmarRevision(gsUrl) : null
            if (!firmada?.url) throw error
            return cargarImagenExportable(firmada.url)
          })
          fuentesRef.current[nombre] = { ...cargada, exportable: true }
        } catch (error) {
          // Sin CORS se puede trazar igual, pero el navegador no deja exportarla
          console.error('La foto no se puede exportar (CORS):', foto.signed_url?.split('?')[0], error)
          const img = await new Promise((resolve) => {
            const i = new window.Image()
            i.onload = () => resolve(i)
            i.onerror = () => resolve(null)
            i.src = foto.signed_url
          })
          if (img) {
            fuentesRef.current[nombre] = {
              objectUrl: foto.signed_url,
              ancho: img.naturalWidth,
              alto: img.naturalHeight,
              img,
              revocar: false,
              exportable: false,
            }
          }
        }
      }),
    )
    setFuentes({ ...fuentesRef.current })
    setCargandoFuentes(false)
  }

  const armarComposicionDesde = (seleccionNombres, cotasMap, extensionesMap, quitar) => {
    const usadas = new Set(seleccionNombres.flatMap((n) => Object.keys(cotasMap[n] || {})))
    const leyenda = [...medidasTrazables, ...medidas.filter((m) => m.esTexto)].map((m) => ({
      letra: m.letra,
      texto: m.texto.toUpperCase(),
      valor: valorPresente(m.valor) ? String(m.valor).trim() : '—',
      trazada: m.esTexto || usadas.has(m.letra),
    }))
    const fotosSalida = seleccionNombres
      .filter((n) => fuentesRef.current[n])
      .map((n) => {
        const foto = { ...fuentesRef.current[n], cotas: cotasMap[n] || {} }
        const e = extensionesMap[n]
        return {
          ...foto,
          recorteAuto: calcularRecorte(foto),
          recorteCompleto: tieneExtension(e)
            ? { x: -e.l, y: -e.t, w: foto.ancho + e.l + e.r, h: foto.alto + e.t + e.b }
            : null,
        }
      })
    return { fotos: fotosSalida, leyenda, nombres: seleccionNombres.filter((n) => fuentesRef.current[n]) }
  }

  const fotosConRecorte = (fotosLista, recortar) =>
    fotosLista.map((f) => ({ ...f, recorte: recortar ? f.recorteAuto : f.recorteCompleto }))

  const capasPorDefecto = (fotosSalida) =>
    posicionesIniciales(fotosSalida).map((p, i) => ({ i, ...p }))

  /**
   * Restaura un snapshot. Por defecto respeta `paso` del JSON.
   * `forzarEditor`: al pulsar Editar desde la JPG guardada se va a Medidas
   * (donde se trazan las cotas), no a la composición.
   */
  const restaurarDesdeJson = async (jsonCrudo, { forzarEditor = false } = {}) => {
    const hidratado = hidratarCotizacion(jsonCrudo, fotosDisponibles)
    if (!hidratado) {
      toast.info('No se pudo restaurar la edición anterior (faltan las fotos).')
      setPaso('seleccion')
      return false
    }
    liberarRecursos()
    setSeleccion(hidratado.seleccion)
    setCotas(hidratado.cotas)
    setExtensiones(hidratado.extensiones)
    setQuitarMargenes(hidratado.quitarMargenes)
    setTextos(hidratado.textos)
    setHistorial([])
    setCotaSeleccionada(null)
    setMedidaActiva(null)
    setComposicion(null)
    setCapas([])
    if (hidratado.medidasManuales?.length) {
      setEspecificacionesLocal(hidratado.medidasManuales)
      setManualLocal(true)
      onMedidasManualesRestauradas?.(hidratado.medidasManuales)
    }
    await cargarFuentesDe(hidratado.seleccion)
    const primera = hidratado.seleccion.find((n) => fuentesRef.current[n]) ?? hidratado.seleccion[0]
    setFotoActiva(primera)
    const irAResultado = !forzarEditor && hidratado.paso === 'resultado'
    if (irAResultado) {
      const comp = armarComposicionDesde(
        hidratado.seleccion,
        hidratado.cotas,
        hidratado.extensiones,
        hidratado.quitarMargenes,
      )
      if (!comp.fotos.length) {
        setPaso('editor')
        return true
      }
      setComposicion(comp)
      const vista = fotosConRecorte(comp.fotos, hidratado.quitarMargenes)
      setCapas(
        hidratado.capas.length === vista.length
          ? hidratado.capas
          : capasPorDefecto(vista),
      )
      setPaso('resultado')
    } else {
      setPaso('editor')
    }
    return true
  }

  // Al abrir: JPG guardada → vista; si no, borrador en json_cotizacion → restaurar;
  // si no hay nada, selección limpia.
  useEffect(() => {
    if (!open) return undefined
    let cancelado = false
    const iniciar = async () => {
      liberarRecursos()
      setHistorial([])
      setMedidaActiva(null)
      setCotaSeleccionada(null)
      setIndicePlantilla(0)
      if (imagenGuardada) {
        resetEstadoLimpio()
        setPaso('guardada')
        return
      }
      const hidratado = hidratarCotizacion(jsonCotizacion, [
        ...fotos.filter((f) => f?.signed_url),
        ...fotosEmpaque.filter((f) => f?.signed_url),
      ])
      if (hidratado) {
        if (cancelado) return
        await restaurarDesdeJson(jsonCotizacion)
        return
      }
      if (cancelado) return
      resetEstadoLimpio()
      setPaso('seleccion')
    }
    iniciar()
    return () => {
      cancelado = true
      liberarRecursos()
    }
    // Solo al abrir
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  // ---------- Paso 1: selección ----------
  const toggleFoto = (nombre) => {
    setSeleccion((prev) => {
      if (prev.includes(nombre)) return prev.filter((n) => n !== nombre)
      if (prev.length >= MAX_FOTOS_COTIZACION) {
        toast.info(`Máximo ${MAX_FOTOS_COTIZACION} fotos`)
        return prev
      }
      return [...prev, nombre]
    })
  }

  const irAEditor = async () => {
    setPaso('editor')
    await cargarFuentesDe(seleccion)
    const primera = seleccion.find((n) => fuentesRef.current[n]) ?? seleccion[0]
    const activa = seleccion.includes(fotoActiva) ? fotoActiva : primera
    setFotoActiva(activa)
    setCotaSeleccionada(null)
    // El usuario elige qué medida trazar; no se preselecciona ninguna.
    setMedidaActiva(null)
  }

  const empezarNueva = () => {
    if (!(especificacionesEfectivas || []).length && onPedirMedidasManuales) {
      onClose()
      onPedirMedidasManuales()
      return
    }
    liberarRecursos()
    resetEstadoLimpio()
    setPaso('seleccion')
  }

  const editarGuardada = async () => {
    const raw = parseJsonCotizacion(jsonCotizacion)
    if (!raw?.seleccion?.length) {
      toast.info('Esta imagen no tiene edición guardada. Usa "Crear nueva" para trazar de nuevo.')
      return
    }
    // Editar = retocar cotas en Medidas; de ahí se puede Generar imagen otra vez.
    const ok = await restaurarDesdeJson(raw, { forzarEditor: true })
    if (!ok) {
      toast.info('No se pudieron cargar las fotos de la edición. Usa "Crear nueva".')
    }
  }

  // ---------- Paso 2: edición ----------
  const guardarHistorial = useCallback(() => {
    setHistorial((h) => [...h.slice(-49), cotas])
  }, [cotas])

  const deshacer = useCallback(() => {
    setHistorial((h) => {
      if (!h.length) return h
      setCotas(h[h.length - 1])
      setCotaSeleccionada(null)
      return h.slice(0, -1)
    })
  }, [])

  const crearCota = (letra, cota) => {
    guardarHistorial()
    const nuevasDeFoto = { ...cotasFoto, [letra]: { ...estiloNuevas, ...cota } }
    setCotas((prev) => ({ ...prev, [fotoActiva]: nuevasDeFoto }))
    // No se pasa sola a la siguiente medida: el usuario elige cuál sigue. La
    // recién trazada queda seleccionada para ajustarla o cambiarle el estilo.
    setMedidaActiva(null)
    setCotaSeleccionada(letra)
  }

  const ajustarCota = (letra, cota) => {
    setCotas((prev) => ({ ...prev, [fotoActiva]: { ...(prev[fotoActiva] || {}), [letra]: cota } }))
  }

  const voltearEtiqueta = (letra) => {
    guardarHistorial()
    setCotas((prev) => {
      const c = prev[fotoActiva]?.[letra]
      if (!c) return prev
      const lado = c.lado || 1
      // Las guías activas se quedan donde están aunque la letra cambie de lado.
      const guias = c.guias > 0 ? { guiasLado: c.guiasLado ?? -lado } : {}
      return { ...prev, [fotoActiva]: { ...prev[fotoActiva], [letra]: { ...c, ...guias, lado: -lado } } }
    })
  }

  // `conHistorial` en false para los sliders: el historial se guarda una vez al
  // tomarlos, no en cada paso del arrastre.
  const recordarEstilo = (cambios) => {
    setEstiloNuevas((prev) => {
      // Se guardan todos los cambios en memoria (para la próxima cota) y solo
      // los campos persistentes van a localStorage.
      const nuevo = { ...prev, ...cambios }
      try {
        window.localStorage.setItem(CLAVE_ESTILO_NUEVAS, JSON.stringify(camposPersistentes(nuevo)))
      } catch {
        // Sin storage solo se recuerda mientras el modal está abierto.
      }
      return nuevo
    })
  }

  const cambiarEstiloCota = (letra, cambios, conHistorial = true) => {
    recordarEstilo(cambios)
    if (!letra || !cotasFoto[letra]) return
    if (conHistorial) guardarHistorial()
    setCotas((prev) => {
      const c = prev[fotoActiva]?.[letra]
      if (!c) return prev
      return { ...prev, [fotoActiva]: { ...prev[fotoActiva], [letra]: { ...c, ...cambios } } }
    })
  }

  // Copia el estilo (no la posición ni el lado de la letra) a todas las cotas
  // de todas las fotos.
  const aplicarEstiloATodas = (letra) => {
    const origen = cotasFoto[letra]
    if (!origen) return
    // El tipo (medida/globo) tampoco se copia: es parte de qué cota es, no de su estilo.
    const { x1, y1, x2, y2, lado, tipo, ...estilo } = { ...ESTILO_COTA_DEFECTO, guiasLado: undefined, ...origen }
    recordarEstilo(estilo)
    guardarHistorial()
    setCotas((prev) =>
      Object.fromEntries(
        Object.entries(prev).map(([foto, deFoto]) => [
          foto,
          Object.fromEntries(Object.entries(deFoto || {}).map(([l, c]) => [l, { ...c, ...estilo }])),
        ]),
      ),
    )
    toast.success('Estilo aplicado a todas las cotas')
  }

  const eliminarCota = useCallback(
    (letra) => {
      if (!fotoActiva || !cotas[fotoActiva]?.[letra]) return
      guardarHistorial()
      setCotas((prev) => {
        const { [letra]: _, ...resto } = prev[fotoActiva] || {}
        return { ...prev, [fotoActiva]: resto }
      })
      setCotaSeleccionada(null)
      setMedidaActiva(letra)
    },
    [cotas, fotoActiva, guardarHistorial],
  )

  const limpiarFoto = () => {
    if (!Object.keys(cotasFoto).length) return
    guardarHistorial()
    setCotas((prev) => ({ ...prev, [fotoActiva]: {} }))
    setCotaSeleccionada(null)
    setMedidaActiva(null)
  }

  const cambiarFoto = (nombre) => {
    setFotoActiva(nombre)
    setCotaSeleccionada(null)
    const deFoto = cotas[nombre] || {}
    setMedidaActiva((actual) => (actual && !deFoto[actual] ? actual : null))
  }

  const elegirMedida = (letra) => {
    setMedidaActiva(letra)
    setCotaSeleccionada(cotasFoto[letra] ? letra : null)
  }

  // Atajos: letra = elegir medida, Supr = borrar cota, Ctrl/Cmd+Z = deshacer
  useEffect(() => {
    if (!open || paso !== 'editor') return undefined
    const onKey = (e) => {
      const tag = e.target?.tagName
      if (tag === 'INPUT' || tag === 'TEXTAREA' || e.target?.isContentEditable) return
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'z') {
        e.preventDefault()
        deshacer()
        return
      }
      if ((e.key === 'Delete' || e.key === 'Backspace') && cotaSeleccionada) {
        e.preventDefault()
        eliminarCota(cotaSeleccionada)
        return
      }
      if (e.key === 'Escape') {
        setCotaSeleccionada(null)
        return
      }
      if (e.metaKey || e.ctrlKey || e.altKey || e.key.length !== 1) return
      // Con letras repetidas (A1, A2...) cada pulsación pasa a la siguiente.
      const candidatas = medidasTrazables.filter((x) => x.letraBase.toUpperCase() === e.key.toUpperCase())
      const actual = candidatas.findIndex((x) => x.letra === medidaActiva)
      const m = candidatas[(actual + 1) % candidatas.length]
      if (m) {
        setMedidaActiva(m.letra)
        setCotaSeleccionada(cotas[fotoActiva]?.[m.letra] ? m.letra : null)
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [open, paso, cotaSeleccionada, deshacer, eliminarCota, medidasTrazables, medidaActiva, cotas, fotoActiva])

  // ---------- Paso 3: generar ----------
  const generar = async () => {
    const usadas = new Set(seleccion.flatMap((n) => Object.keys(cotas[n] || {})))
    // Todas las medidas van en la tabla, estén o no trazadas en las fotos: las
    // no trazadas quedan como dato informativo (letra en gris).
    const leyenda = [...medidasTrazables, ...medidas.filter((m) => m.esTexto)].map((m) => ({
      letra: m.letra,
      texto: m.texto.toUpperCase(),
      valor: valorPresente(m.valor) ? String(m.valor).trim() : '—',
      trazada: m.esTexto || usadas.has(m.letra),
    }))
    const bloqueadas = seleccion.filter((n) => fuentesRef.current[n] && !fuentesRef.current[n].exportable)
    if (bloqueadas.length) {
      toast.error(
        `El navegador bloqueó la descarga de ${plural(bloqueadas.length, 'foto', 'fotos')} (permisos CORS del almacenamiento). Quítala${bloqueadas.length > 1 ? 's' : ''} con "Cambiar fotos" o recarga la página e intenta de nuevo.`,
      )
      return
    }
    const fotosSalida = seleccion
      .filter((n) => fuentesRef.current[n])
      .map((n) => {
        const foto = { ...fuentesRef.current[n], cotas: cotas[n] || {} }
        const e = extensiones[n]
        return {
          ...foto,
          recorteAuto: calcularRecorte(foto),
          // Sin quitar márgenes se muestra la foto más el espacio extra del editor,
          // para que las cotas que se salen de la foto no queden cortadas.
          recorteCompleto: tieneExtension(e)
            ? { x: -e.l, y: -e.t, w: foto.ancho + e.l + e.r, h: foto.alto + e.t + e.b }
            : null,
        }
      })

    // Si las fotos son las mismas (solo se retocaron cotas) se conserva la
    // posición que el usuario les dio; si cambiaron, se parte del orden por defecto.
    const mismasFotos =
      composicion &&
      composicion.nombres.length === fotosSalida.length &&
      composicion.nombres.every((n, i) => n === seleccion.filter((x) => fuentesRef.current[x])[i])
    setComposicion({
      fotos: fotosSalida,
      leyenda,
      nombres: seleccion.filter((n) => fuentesRef.current[n]),
    })
    const vista = fotosConRecorte(fotosSalida, quitarMargenes)
    if (!mismasFotos) {
      setCapas(capasPorDefecto(vista))
    } else {
      // Con cotas retocadas el recorte puede cambiar de proporción: se
      // conserva el ancho y la posición y se ajusta el alto.
      setCapas((prev) =>
        prev.map((c) => {
          const v = tamanoVisible(vista[c.i])
          const h = (c.w * v.h) / v.w
          return { ...c, h, y: Math.max(0, Math.min(c.y, SALIDA_ALTO - h)) }
        }),
      )
    }
    setPaso('resultado')
  }

  const fotosVista = useMemo(
    () => (composicion ? fotosConRecorte(composicion.fotos, quitarMargenes) : []),
    [composicion, quitarMargenes],
  )

  const cambiarQuitarMargenes = (valor) => {
    setQuitarMargenes(valor)
    // Cambia el tamaño de cada foto: se vuelve a la distribución por defecto.
    if (composicion) setCapas(capasPorDefecto(fotosConRecorte(composicion.fotos, valor)))
  }

  // La URL firmada es de otro dominio y <a download> la ignora; se baja como
  // blob. Si el bucket bloquea CORS, se abre en otra pestaña.
  const descargarGuardada = async () => {
    try {
      const resp = await fetch(imagenGuardada)
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`)
      const url = URL.createObjectURL(await resp.blob())
      const link = document.createElement('a')
      link.href = url
      link.download = `${nombreArchivo}_cotizacion.jpg`
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      URL.revokeObjectURL(url)
    } catch (error) {
      console.error('Error descargando imagen de cotización:', error)
      window.open(imagenGuardada, '_blank', 'noopener')
    }
  }

  const persistirBorrador = async (pasoOverride) => {
    if (!hayTrabajoEditable || !onPersistirJson) return true
    try {
      await onPersistirJson(snapshotActual(pasoOverride))
      return true
    } catch (error) {
      console.error('Error guardando borrador de cotización:', error)
      toast.error('No se pudo guardar el borrador de la edición')
      return false
    }
  }

  const guardar = async () => {
    if (!composicion) return
    setGuardando(true)
    try {
      const blob = await generarImagenCotizacion(
        capas.map((c) => fotosVista[c.i]),
        composicion.leyenda,
        capas,
        textos,
      )
      const json = snapshotActual('resultado')
      const ok = await onGuardar?.(blob, json)
      if (ok) {
        toast.success('Imagen de cotización guardada')
        onClose()
      } else {
        toast.error('No se pudo guardar la imagen de cotización')
      }
    } catch (error) {
      console.error('Error generando imagen de cotización:', error)
      toast.error('No se pudo generar la imagen. Intenta de nuevo.')
    } finally {
      setGuardando(false)
    }
  }

  const salirYPersistir = async () => {
    await persistirBorrador()
    onClose()
  }

  const cerrar = () => {
    if (paso === 'editor' && totalCotas > 0) {
      Modal.confirm({
        title: '¿Salir de la edición?',
        content: 'Se guardará un borrador para que puedas continuar después.',
        okText: 'Salir',
        cancelText: 'Seguir editando',
        onOk: salirYPersistir,
      })
      return
    }
    if (paso === 'resultado' && hayTrabajoEditable) {
      salirYPersistir()
      return
    }
    onClose()
  }

  // ---------- Render ----------
  const medidaActual = medidasTrazables.find((m) => m.letra === medidaActiva)
  const ayuda = (() => {
    const trazandoCirc = (estiloNuevas?.tipo || ESTILO_COTA_DEFECTO.tipo) === 'circunferencia'
    if (!medidasTrazables.length) {
      return {
        tipo: 'info',
        texto: esManual
          ? 'No hay medidas para trazar. Cierra y vuelve a agregar medidas manualmente.'
          : 'No hay medidas para trazar.',
      }
    }
    if (modoBorrador === 'clic' && medidaActual)
      return {
        tipo: 'accion',
        texto: trazandoCirc ? (
          <>Haz clic en el <b>borde</b> de la circunferencia <b>{medidaActual.letra}</b> · Esc para cancelar</>
        ) : (
          <>Haz clic donde <b>termina</b> la medida <b>{medidaActual.letra}</b> · Esc para cancelar</>
        ),
      }
    if (cotaSeleccionada)
      return {
        tipo: 'info',
        texto: <>Arrastra los puntos para ajustar <b>{cotaSeleccionada}</b> · doble clic en la letra para cambiarla de lado · Supr para borrar</>,
      }
    if (medidaActual) {
      const yaTrazada = !!cotasFoto[medidaActual.letra]
      return {
        tipo: 'accion',
        texto: (
          <>
            {trazandoCirc
              ? yaTrazada
                ? 'Vuelve a trazar (centro · borde)'
                : 'Clic en el centro y luego en el borde para trazar'
              : yaTrazada
                ? 'Vuelve a trazar'
                : 'Arrastra sobre la foto para trazar'}{' '}
            <b>{medidaActual.letra}</b>
            <span className="cotz-ayuda__desc"> · {medidaActual.texto}</span>
          </>
        ),
      }
    }
    if (medidasTrazables.some((m) => !cotasFoto[m.letra]))
      return { tipo: 'info', texto: 'Elige en el panel (o con su letra en el teclado) la medida que vas a trazar.' }
    return { tipo: 'ok', texto: 'Todas las medidas están en esta foto. Puedes ajustarlas o pasar a otra foto.' }
  })()

  const titulo = (
    <div className="cotz-titulo">
      <span className="cotz-titulo__texto">Imagen para cotización</span>
      {paso !== 'guardada' && (
        <Steps
          size="small"
          current={PASOS.indexOf(paso)}
          className="cotz-titulo__pasos"
          items={[{ title: 'Fotos' }, { title: 'Medidas' }, { title: 'Guardar' }]}
        />
      )}
    </div>
  )

  const renderSeleccion = () => (
    <div className="cotz-seleccion">
      <p className="cotz-seleccion__intro">
        Elige de <b>1 a {MAX_FOTOS_COTIZACION}</b> fotos. Aparecerán en la imagen final en el orden en que las marques.
      </p>
      {[
        ['Fotos del material', fotosMaterial],
        ['Imágenes de empaque', fotosDeEmpaque],
      ]
        .filter(([, lista]) => lista.length)
        .map(([titulo, lista]) => (
          <section key={titulo} className="cotz-seleccion__seccion">
            {fotosMaterial.length > 0 && fotosDeEmpaque.length > 0 && (
              <h4 className="cotz-seleccion__titulo">{titulo}</h4>
            )}
            <div className="cotz-seleccion__grid">
              {lista.map((foto) => {
                const orden = seleccion.indexOf(foto.nombre_objeto)
                const elegida = orden >= 0
                return (
                  <button
                    key={foto.nombre_objeto}
                    type="button"
                    className={`cotz-foto-opcion ${elegida ? 'is-elegida' : ''}`}
                    onClick={() => toggleFoto(foto.nombre_objeto)}
                    aria-pressed={elegida}
                  >
                    <img src={foto.signed_url} alt="" />
                    <span className="cotz-foto-opcion__marca">{elegida ? orden + 1 : ''}</span>
                    {elegida && <span className="cotz-foto-opcion__check"><CheckOutlined /></span>}
                  </button>
                )
              })}
            </div>
          </section>
        ))}
    </div>
  )

  const urlPlantilla = imagenesPlantilla[indicePlantilla] ?? null

  const renderEditor = () => (
    <div className="cotz-editor">
      <aside className="cotz-panel">
        <section className="cotz-ref">
          <header className="cotz-panel__header">
            <span>Plantilla de referencia</span>
            {imagenesPlantilla.length > 1 && (
              <span className="cotz-ref__nav">
                <Button
                  size="small"
                  type="text"
                  icon={<LeftOutlined />}
                  onClick={() => setIndicePlantilla((i) => (i - 1 + imagenesPlantilla.length) % imagenesPlantilla.length)}
                />
                {indicePlantilla + 1}/{imagenesPlantilla.length}
                <Button
                  size="small"
                  type="text"
                  icon={<RightOutlined />}
                  onClick={() => setIndicePlantilla((i) => (i + 1) % imagenesPlantilla.length)}
                />
              </span>
            )}
          </header>
          <div className="cotz-ref__img">
            {cargandoPlantilla ? (
              <Spin />
            ) : urlPlantilla ? (
              <Image src={urlPlantilla} alt="Plantilla de medidas" preview={{ mask: 'Ampliar' }} />
            ) : (
              <span className="cotz-ref__vacio"><PictureOutlined /> Sin imagen de referencia</span>
            )}
          </div>
        </section>

        <section className="cotz-medidas">
          <header className="cotz-panel__header">
            <span>Medidas</span>
            <span className="cotz-medidas__conteo">
              {Object.keys(cotasFoto).length}/{medidasTrazables.length} en esta foto
            </span>
          </header>
          <ul className="cotz-medidas__lista">
            {medidasTrazables.map((m) => {
              const enFoto = !!cotasFoto[m.letra]
              const enCuantas = seleccion.filter((n) => cotas[n]?.[m.letra]).length
              return (
                <li key={m.letra}>
                  <button
                    type="button"
                    className={`cotz-medida ${medidaActiva === m.letra ? 'is-activa' : ''} ${enFoto ? 'is-trazada' : ''}`}
                    onClick={() => elegirMedida(m.letra)}
                  >
                    <span className="cotz-medida__letra">{m.letra}</span>
                    <span className="cotz-medida__texto">{m.texto || '—'}</span>
                    <span className="cotz-medida__estado">
                      {enFoto ? (
                        <CheckCircleFilled />
                      ) : (
                        enCuantas > 0 && seleccion.length > 1 && (
                          <Tooltip title={`Trazada en ${plural(enCuantas, 'foto', 'fotos')}`}>
                            <span className="cotz-medida__otras">{enCuantas}</span>
                          </Tooltip>
                        )
                      )}
                    </span>
                  </button>
                  {enFoto && (
                    <Tooltip title="Quitar de esta foto">
                      <button type="button" className="cotz-medida__quitar" onClick={() => eliminarCota(m.letra)}>
                        <CloseOutlined />
                      </button>
                    </Tooltip>
                  )}
                </li>
              )
            })}
          </ul>
          <p className="cotz-medidas__tip">
            Tip: presiona la letra en el teclado para elegirla. Mantén <kbd>Shift</kbd> para trazar en ángulo libre.
            Usa <kbd>Ctrl</kbd>/<kbd>⌘</kbd> + rueda para hacer zoom en medidas pequeñas.
          </p>
        </section>
      </aside>

      <main className="cotz-area">
        <div className="cotz-pestanas">
          {seleccion.map((nombre, i) => {
            const n = Object.keys(cotas[nombre] || {}).length
            const fuente = fuentes[nombre]
            return (
              <button
                key={nombre}
                type="button"
                className={`cotz-pestana ${fotoActiva === nombre ? 'is-activa' : ''}`}
                onClick={() => cambiarFoto(nombre)}
                disabled={!fuente}
              >
                {fuente && <img src={fuente.objectUrl} alt="" />}
                <span>Foto {i + 1}</span>
                {fuente && !fuente.exportable && (
                  <Tooltip title="Esta foto no se pudo descargar con permisos (CORS) y no se podrá exportar">
                    <WarningFilled className="cotz-pestana__alerta" />
                  </Tooltip>
                )}
                <span className={`cotz-pestana__conteo ${n ? 'has-cotas' : ''}`}>{n}</span>
              </button>
            )
          })}
        </div>

        <div className="cotz-barra">
          <div className={`cotz-ayuda cotz-ayuda--${ayuda.tipo}`}>
            {ayuda.tipo === 'ok' ? <CheckCircleFilled /> : <EditOutlined />}
            <span>{ayuda.texto}</span>
          </div>
          <div className="cotz-barra__acciones">
            <Tooltip title="Deshacer (Ctrl/Cmd + Z)">
              <Button icon={<UndoOutlined />} onClick={deshacer} disabled={!historial.length} />
            </Tooltip>
            <Tooltip title="Borrar cota seleccionada (Supr)">
              <Button
                icon={<DeleteOutlined />}
                onClick={() => eliminarCota(cotaSeleccionada)}
                disabled={!cotaSeleccionada}
              />
            </Tooltip>
            <Tooltip title="Quitar todas las cotas de esta foto">
              <Button icon={<ClearOutlined />} onClick={limpiarFoto} disabled={!Object.keys(cotasFoto).length}>
                Limpiar
              </Button>
            </Tooltip>
          </div>
        </div>

        {(() => {
          // Con cota trazada se edita esa; si solo hay medida elegida, se
          // configura el estilo con el que se va a trazar (tipo incluido).
          const letraTrazada = cotasFoto[cotaSeleccionada] ? cotaSeleccionada : null
          const letra = letraTrazada || medidaActiva
          const pendiente = !letraTrazada && !!medidaActiva
          return (
            <BarraEstiloCota
              letra={letra}
              pendiente={pendiente}
              estilo={{
                ...ESTILO_COTA_DEFECTO,
                ...(letraTrazada ? cotasFoto[letraTrazada] : estiloNuevas),
              }}
              vertical={letraTrazada ? cotaEsVertical(cotasFoto[letraTrazada]) : false}
              onCambio={(cambios, conHistorial = true) =>
                cambiarEstiloCota(letraTrazada, cambios, conHistorial)
              }
              onGuardarHistorial={guardarHistorial}
              onRestablecer={() => {
                const { tipo, ...defecto } = ESTILO_COTA_DEFECTO
                cambiarEstiloCota(letraTrazada, { ...defecto, guiasLado: undefined })
              }}
              onAplicarATodas={letraTrazada ? () => aplicarEstiloATodas(letraTrazada) : undefined}
            />
          )
        })()}

        {cargandoFuentes || !fotoActiva || !fuentes[fotoActiva] ? (
          <div className="cotz-escenario cotz-escenario--cargando">
            {cargandoFuentes ? (
              <>
                <Spin />
                <span>Cargando fotos...</span>
              </>
            ) : (
              <span>No se pudo cargar la foto</span>
            )}
          </div>
        ) : (
          <LienzoFoto
            estiloNuevas={estiloNuevas}
            extension={extensiones[fotoActiva] || SIN_EXTENSION}
            onExtender={(nueva) => setExtensiones((prev) => ({ ...prev, [fotoActiva]: nueva }))}
            key={fotoActiva}
            fuente={fuentes[fotoActiva]}
            cotas={cotasFoto}
            medidaActiva={medidaActiva}
            cotaSeleccionada={cotaSeleccionada}
            onCrear={crearCota}
            onIniciarAjuste={guardarHistorial}
            onAjustar={ajustarCota}
            onSeleccionar={setCotaSeleccionada}
            onVoltear={voltearEtiqueta}
            onBorradorChange={setModoBorrador}
          />
        )}
      </main>
    </div>
  )

  const renderResultado = () =>
    composicion && (
      <ComposicionCotizacion
        fotos={fotosVista}
        leyenda={composicion.leyenda}
        capas={capas}
        onChange={setCapas}
        textos={textos}
        onTextosChange={setTextos}
      />
    )

  const footer = (() => {
    if (paso === 'guardada') {
      const puedeEditar = !!hidratarCotizacion(jsonCotizacion, fotosDisponibles)
      return (
        <div className="cotz-footer">
          <span />
          <div className="cotz-footer__der">
            <Button onClick={onClose}>Cerrar</Button>
            <Button icon={<DownloadOutlined />} onClick={descargarGuardada}>
              Descargar
            </Button>
            {puedeEditar && (
              <Button icon={<EditOutlined />} onClick={editarGuardada}>
                Editar
              </Button>
            )}
            <Button
              type="primary"
              icon={<PlusOutlined />}
              disabled={!puedeCrearNueva}
              title={puedeCrearNueva ? undefined : motivoNoCrear}
              onClick={empezarNueva}
            >
              Crear nueva
            </Button>
          </div>
        </div>
      )
    }
    if (paso === 'seleccion') {
      return (
        <div className="cotz-footer">
          <span />
          <div className="cotz-footer__der">
            <Button onClick={cerrar}>Cancelar</Button>
            <Button type="primary" disabled={!seleccion.length} onClick={irAEditor}>
              Continuar con {plural(seleccion.length, 'foto', 'fotos')} <ArrowRightOutlined />
            </Button>
          </div>
        </div>
      )
    }
    if (paso === 'editor') {
      return (
        <div className="cotz-footer">
          <div className="cotz-footer__izq">
            <Button type="text" icon={<ArrowLeftOutlined />} onClick={() => setPaso('seleccion')}>
              Cambiar fotos
            </Button>
          </div>
          <div className="cotz-footer__der">
            <span className="cotz-footer__resumen">
              {totalCotas
                ? `${plural(totalCotas, 'cota', 'cotas')} en ${plural(fotosConCotas, 'foto', 'fotos')}`
                : 'Aún no hay cotas trazadas'}
            </span>
            <Button type="primary" onClick={generar} disabled={cargandoFuentes}>
              Generar imagen
            </Button>
          </div>
        </div>
      )
    }
    return (
      <div className="cotz-footer">
        <div className="cotz-footer__izq">
          <Button type="text" icon={<ArrowLeftOutlined />} onClick={() => setPaso('editor')}>
            Volver a editar
          </Button>
          <Button icon={<UndoOutlined />} onClick={() => setCapas(capasPorDefecto(fotosVista))}>
            Restablecer posición
          </Button>
          <Tooltip title="Recorta cada foto a la pieza y sus medidas, sin el fondo sobrante">
            <span className="cotz-footer__opcion">
              <Switch size="small" checked={quitarMargenes} onChange={cambiarQuitarMargenes} />
              Quitar márgenes
            </span>
          </Tooltip>
        </div>
        <div className="cotz-footer__der">
          <span className="cotz-footer__resumen">Arrastra fotos y textos para moverlos · esquinas para el tamaño · doble clic para editar un texto</span>
          <Button onClick={onClose}>Cerrar</Button>
          <Button type="primary" icon={<SaveOutlined />} onClick={guardar} loading={guardando}>
            Guardar imagen
          </Button>
        </div>
      </div>
    )
  })()

  return (
    <Modal
      open={open}
      onCancel={cerrar}
      title={titulo}
      footer={footer}
      width={paso === 'seleccion' ? 760 : 'min(1480px, 96vw)'}
      style={{ top: 16 }}
      className={`cotz-modal cotz-modal--${paso}`}
      keyboard={false}
      maskClosable={false}
      destroyOnClose
    >
      {paso === 'guardada' && (
        <div className="cotz-resultado">
          <img src={imagenGuardada} alt="Imagen para cotización guardada" />
        </div>
      )}
      {paso === 'seleccion' && renderSeleccion()}
      {paso === 'editor' && renderEditor()}
      {paso === 'resultado' && renderResultado()}
    </Modal>
  )
}

export default ModalImagenCotizacion
